import React from 'react';
import {
  Box,
  Drawer,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  Storefront as ProductsIcon,
  Analytics as AnalyticsIcon,
  Person as ProfileIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  QuestionAnswer as QuestionsIcon,
  Reviews as ReviewsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { VendorProfile } from '../../services/vendorProfileService';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { useVendorProfile } from '../../contexts/VendorProfileContext';

// S3 asset URLs
const logoImage = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';
const burgerPlaceholder = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/burger-placeholder-small.png';

const drawerWidth = 250;

interface CompanyProfileSidebarProps {
  vendorProfile?: VendorProfile | null;
}

const CompanyProfileSidebar: React.FC<CompanyProfileSidebarProps> = ({ vendorProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useVendorAuth();
  const { profileImageUrl } = useVendorProfile();

  // Use context image if available, otherwise fall back to vendorProfile
  const displayImageUrl = profileImageUrl || vendorProfile?.profileImageUrl || burgerPlaceholder;

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Orders', icon: <OrdersIcon />, path: '/orders', badge: null as unknown as number },
    { text: 'Transactions', icon: <AnalyticsIcon />, path: '/transactions' },
    { text: 'Products', icon: <ProductsIcon />, path: '/products' },
    { text: 'Reviews', icon: <ReviewsIcon />, path: '/reviews' },
    { text: 'Questions', icon: <QuestionsIcon />, path: '/questions' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
    { text: 'Company profile', icon: <ProfileIcon />, path: '/company-profile' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Logout', icon: <LogoutIcon />, isLogout: true },
  ];

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #f1f4f4',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 4, 
        alignItems: 'center', 
        px: 5, 
        py: 3,
        height: '100%'
      }}>
        {/* Company Profile Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center', width: '100%' }}>
          <Box
            sx={{
              width: '100%',
              height: 75,
              backgroundImage: `url(${displayImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '16px',
              mixBlendMode: 'multiply'
            }}
          />
          <Typography 
            sx={{ 
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#181818',
              letterSpacing: '-0.16px',
              textAlign: 'center',
              width: '100%'
            }}
          >
            {vendorProfile?.companyName || 'Company Name'}
          </Typography>
        </Box>

        {/* Navigation Menu */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '202px', flexGrow: 1 }}>
          {menuItems.map((item) => {
            const isActive = item.path
              ? location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
              : false;
            
            const handleClick = () => {
              if (item.isLogout) {
                logout();
                navigate('/login');
              } else if (item.path) {
                navigate(item.path);
              }
            };
            
            return (
              <ListItem
                key={item.text}
                onClick={handleClick}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '12px',
                  px: 1.5,
                  py: 1.5,
                  backgroundColor: isActive ? '#f6f8f8' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive ? '#f6f8f8' : '#f1f4f4',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 24, mr: 2 }}>
                  {item.badge ? (
                    <Badge 
                      badgeContent={item.badge} 
                      color="success"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#426a53',
                          fontSize: '12px',
                          height: '16px',
                          minWidth: '16px',
                        }
                      }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: '16px',
                    fontWeight: isActive ? 700 : 400,
                    color: '#181818',
                    lineHeight: 1.4,
                    letterSpacing: isActive ? '-0.16px' : 'normal',
                  }}
                />
              </ListItem>
            );
          })}
        </Box>

        {/* Eventi Logo */}
        <Box sx={{ 
          width: '120px', 
          height: '94px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mt: 'auto'
        }}>
          <Box
            component="img"
            src={logoImage}
            alt="Eventi Logo"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default CompanyProfileSidebar;
